import { instanceOfErrorListResponse, Service, type ErrorListResponse } from '@genial/services';
import { useRoute, type RouteProp } from '@react-navigation/native';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../hooks/useToastCreator';
import type { SignedInRootStackParamList } from '../../../routes/types';
import {
  batchTicketRedeemRowsSummaryQuery,
  batchTicketRedeemSummaryQuery,
  type BatchTicketRedeemSummaryQueryResponse,
  type BatchTicketRedeemRowsSummaryQueryResponse,
} from '../../../store/batchTicket/selectors/redeem';
import { useErrorToast } from '../hooks/toast-errpr';
import {
  screenName,
  errorExportRedeemSpreadsheetSentMessage,
  errorExportRedeemSpreadsheetErrorMessage,
} from '../utils/constants';

interface RedeemSummaryContextType {
  isLoadingRedeemSummary: boolean;
  handleExportErrorRegister: () => Promise<void>;
  redeemDetailsRecords?: BatchTicketRedeemSummaryQueryResponse;
  isExportingErrorRegister: boolean;
  handleExportSentRegister: () => Promise<void>;
  isExportingSentRegister: boolean;
  redeemSummaryRefresher: () => void;
  redeemSummaryIsError: boolean;
  redeemRowsDetailsRecords?: BatchTicketRedeemRowsSummaryQueryResponse;
  redeemRowsSummaryIsError: boolean;
  isLoadingRedeemRowsSummary: boolean;
  redeemRowsSummaryRefresher: () => void;
}

const RedeemSummaryContext = createContext<RedeemSummaryContextType>(
  {} as RedeemSummaryContextType
);

export const RedeemSummaryProvider = ({ children }: { children: React.ReactNode }) => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'RedeemBatchTicketDetails'>>();
  const { handleErrorToast } = useErrorToast();
  const {
    data: redeemSummaryResponse,
    error: redeemSummaryIsError,
    loading: isLoadingRedeemSummary,
    refresher: redeemSummaryRefresher,
  } = useRecoilQuery(batchTicketRedeemSummaryQuery({ basketAdvisorId: params.basketAdvisorId }), {
    cachePolicy: 'no-cache',
  });

  const {
    data: redeemRowsSummaryResponse,
    error: redeemRowsSummaryIsError,
    loading: isLoadingRedeemRowsSummary,
    refresher: redeemRowsSummaryRefresher,
  } = useRecoilQuery(
    batchTicketRedeemRowsSummaryQuery({ basketAdvisorId: params.basketAdvisorId }),
    {
      cachePolicy: 'no-cache',
    }
  );

  const [isExportingErrorRegister, setIsExportingErrorRegister] = useState(false);
  const [isExportingSentRegister, setIsExportingSentRegister] = useState(false);

  const { showToast, showToastFromError } = useToastCreator('toast-summary-redeem-ticket');

  const handleExportErrorRegister = async () => {
    try {
      setIsExportingErrorRegister(true);

      const response = await Service.Basket.V1.getDownloadFileErrorBasketAdvisorId({
        screenName,
        basketAdvisorOrderId: params.basketAdvisorId,
      });

      const signedGetUrl = response.data?.preSignedUrl;

      if (!signedGetUrl) {
        console.error('Erro na url: ', signedGetUrl);
        throw new Error('Url invalida');
      }

      window.open(signedGetUrl, '_blank');
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorExportRedeemSpreadsheetErrorMessage);
    } finally {
      setIsExportingErrorRegister(false);
      false;
    }
  };

  const handleExportSentRegister = async () => {
    try {
      setIsExportingSentRegister(true);

      const response = await Service.Basket.V1.postDownloadFileMonitoring({
        screenName,
        basketMonitoringFileRequest: {
          basketAdvisorOrderId: params.basketAdvisorId,
        },
      });

      const signedGetUrl = response.data?.preSignedUrl;

      if (!signedGetUrl) {
        console.error('Erro na url: ', signedGetUrl);
        throw new Error('Url invalida');
      }

      window.open(signedGetUrl, '_blank');
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorExportRedeemSpreadsheetSentMessage);
    } finally {
      setIsExportingSentRegister(false);
    }
  };

  useEffect(() => {
    handleErrorToast(redeemSummaryIsError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redeemSummaryIsError]);

  return (
    <RedeemSummaryContext.Provider
      value={{
        isLoadingRedeemSummary,
        handleExportErrorRegister,
        redeemDetailsRecords: redeemSummaryResponse,
        isExportingErrorRegister,
        handleExportSentRegister,
        isExportingSentRegister,
        redeemSummaryRefresher,
        redeemSummaryIsError: !!redeemSummaryIsError,
        redeemRowsDetailsRecords: redeemRowsSummaryResponse,
        redeemRowsSummaryIsError: !!redeemRowsSummaryIsError,
        isLoadingRedeemRowsSummary,
        redeemRowsSummaryRefresher,
      }}>
      {children}
    </RedeemSummaryContext.Provider>
  );
};

export const useRedeemSummaryContext = (): RedeemSummaryContextType => {
  const context = useContext(RedeemSummaryContext);

  if (!context) {
    throw new Error('useRedeemSummaryContext must be used within an RedeemSummaryContext');
  }
  return context;
};
